import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for WebSocket connection to receive real-time lead alerts
 * @param {string} workspaceId - The workspace ID to subscribe to
 * @param {Object} options - Configuration options
 */
export function useWebSocket(workspaceId, options = {}) {
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const { onMessage, onConnect, onDisconnect } = options;

  const connect = useCallback(() => {
    if (!workspaceId) return;

    // Convert HTTP URL to WebSocket URL
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const url = `${wsUrl}/api/ws/${workspaceId}`;

    try {
      ws.current = new WebSocket(url);

      ws.current.onopen = () => {
        console.log('WebSocket connected');
        onConnect?.();
        
        // Send periodic pings to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send('ping');
          }
        }, 30000);

        ws.current.pingInterval = pingInterval;
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleAlert(data);
          onMessage?.(data);
        } catch (e) {
          // Ignore non-JSON messages (like pong)
        }
      };

      ws.current.onclose = () => {
        console.log('WebSocket disconnected');
        onDisconnect?.();
        clearInterval(ws.current?.pingInterval);
        
        // Reconnect after 3 seconds
        reconnectTimeout.current = setTimeout(() => {
          connect();
        }, 3000);
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [workspaceId, onMessage, onConnect, onDisconnect]);

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimeout.current);
      clearInterval(ws.current?.pingInterval);
      ws.current?.close();
    };
  }, [connect]);

  return ws.current;
}

/**
 * Handle incoming WebSocket alerts
 * @param {Object} data - The alert data
 */
function handleAlert(data) {
  switch (data.type) {
    case 'HOT_LEAD_ALERT':
      handleHotLeadAlert(data);
      break;
    case 'NEW_LEAD':
      handleNewLeadAlert(data);
      break;
    case 'CALL_COMPLETED':
      handleCallAlert(data);
      break;
    case 'DEAL_CLOSED':
      handleDealClosedAlert(data);
      break;
    case 'STAGE_CHANGED':
      handleStageChangeAlert(data);
      break;
    default:
      break;
  }
}

function handleHotLeadAlert(data) {
  // Browser notification
  if (Notification.permission === 'granted') {
    new Notification('🔥 Hot Lead Alert!', {
      body: data.message,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: `hot-lead-${data.lead_id}`,
      requireInteraction: true,
    });
  }

  // Toast notification
  toast.custom(
    (t) => (
      <div
        style={{
          background: 'linear-gradient(135deg, #1a2332 0%, #0f1419 100%)',
          border: '2px solid #ef4444',
          borderRadius: '12px',
          padding: '16px',
          color: 'white',
          maxWidth: '360px',
          boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
        }}
      >
        <div
          style={{
            color: '#ef4444',
            fontWeight: 'bold',
            fontSize: '16px',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          🔥 Hot Lead Alert!
        </div>
        <div style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.5' }}>
          {data.message}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a
            href={`/leads?id=${data.lead_id}`}
            style={{
              background: '#ef4444',
              color: 'white',
              padding: '10px 16px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              flex: 1,
              textAlign: 'center',
              fontSize: '14px',
            }}
          >
            📞 Call Now
          </a>
          <button
            onClick={() => toast.dismiss(t)}
            style={{
              background: 'transparent',
              border: '1px solid #94a3b8',
              color: '#94a3b8',
              padding: '10px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Later
          </button>
        </div>
      </div>
    ),
    { duration: 30000 }
  );

  // Play alert sound
  playAlertSound();
}

function handleNewLeadAlert(data) {
  toast.success(data.message, {
    duration: 5000,
    icon: '🆕',
  });
}

function handleCallAlert(data) {
  toast.info(data.message, {
    duration: 5000,
    icon: '📞',
  });
}

function handleDealClosedAlert(data) {
  if (data.won) {
    toast.success(data.message, {
      duration: 8000,
      icon: '🎉',
    });
    // Confetti effect could be added here
  } else {
    toast.error(data.message, {
      duration: 5000,
      icon: '😞',
    });
  }
}

function handleStageChangeAlert(data) {
  toast(data.message, {
    duration: 4000,
    icon: '📊',
  });
}

function playAlertSound() {
  try {
    const audio = new Audio('/alert.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Ignore autoplay restrictions
    });
  } catch (e) {
    // Audio not available
  }
}

/**
 * Request browser notification permission
 */
export function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

export default useWebSocket;
